import type { AuditSection } from "@/types/audit";

/**
 * Canonical overall-score formula for the free scan.
 *
 * Why this lives here, not in the n8n workflow:
 *   The workflow currently produces TWO different aggregates — the JSON
 *   envelope `score` (rebalanced/floored) and the score baked inside
 *   `htmlReport`'s big gauge (raw mean of the four sections). They drift
 *   because only one of the two paths got patched after the per-section
 *   scoring rules changed. Computing the aggregate on the React side from
 *   the per-section scores makes the page and the email mathematically
 *   identical, and lets us pick a transparent weighting we can actually
 *   defend to a buyer.
 *
 * The weighting:
 *   nLPD enforcement targets the things a regulator can actually point to
 *   in a public site. Transparency (Art. 19-21) is the foundation of the
 *   law — without it, none of the rest stands. Cookies/tracking
 *   (Art. 45c LTC) is the highest-litigation surface today. Data-subject
 *   rights (Art. 25-29) MUST appear in any compliant policy. Swiss-specific
 *   awareness (PFPDT mentions, nLPD references) is a softer signal — useful
 *   but not a fail.
 *
 *   Transparency      35%
 *   Cookies           30%
 *   Rights            25%
 *   Swiss-specific    10%
 *
 * NA categories (those `isNA: true` because the surface scan can't see
 * them) are excluded and the remaining weights renormalised — so a missing
 * category never silently drags the score to zero.
 */

interface WeightSpec {
  pattern: RegExp;
  weight: number;
}

const SECTION_WEIGHTS: WeightSpec[] = [
  { pattern: /transparen|information/i, weight: 0.35 },
  { pattern: /cookie|tracking/i, weight: 0.3 },
  { pattern: /droit|rights/i, weight: 0.25 },
  { pattern: /suisse|swiss|sp[eé]cifique/i, weight: 0.1 },
];

const DEFAULT_WEIGHT = 0.1;

const weightFor = (sectionName: string): number => {
  for (const { pattern, weight } of SECTION_WEIGHTS) {
    if (pattern.test(sectionName)) return weight;
  }
  return DEFAULT_WEIGHT;
};

/**
 * Compute the overall 0-100 score from the per-section scores.
 *
 * Rules:
 *   - Skip sections with `isNA: true` (they can't be assessed from a
 *     surface scan and shouldn't drag the score down).
 *   - Skip sections where `score === null`.
 *   - Weighted mean per the SECTION_WEIGHTS table, with weights
 *     renormalised over the kept sections.
 *   - If no scoreable sections remain, return 0 (caller usually surfaces
 *     a "scan incomplete" UI in that case).
 */
export const computeOverallScore = (sections: AuditSection[]): number => {
  const scoreable = (sections || []).filter(
    (s) => !s.isNA && typeof s.score === "number" && s.score !== null,
  );
  if (scoreable.length === 0) return 0;

  let weightedSum = 0;
  let totalWeight = 0;
  for (const s of scoreable) {
    const w = weightFor(s.name);
    weightedSum += (s.score ?? 0) * w;
    totalWeight += w;
  }
  if (totalWeight === 0) return 0;
  return Math.round(weightedSum / totalWeight);
};

/**
 * Map a numeric score to the standard French-side label used everywhere
 * else in the codebase. Kept in sync with `getScoreColor` in
 * `AuditResults.tsx`.
 */
export const deriveScoreLabel = (score: number): string => {
  if (score >= 70) return "Bon";
  if (score >= 40) return "À améliorer";
  return "Critique";
};
